import { FieldViewModel, convertGeometryToOpenLayers, Datalayer, DatalayersMap, DrawingLayer, IDataLayer, IDataLayerType, IHolding, LayerEditorConfig, LayerTypeId, LayerViewModel } from "../omnia"
import { IDatalayersService } from "../types"
import { EditLayerCmd } from "./editCmd"
import { ILayerValidationResults, IZoneEditor } from "./types"

import m = omnia.models;
import { ol_geom } from "@omnia/exports/openlayers";

export class EditContext {

    public target: IDataLayer

    public outerGeometry: ol_geom.Geometry

    public config: LayerEditorConfig

    readonly holding: IHolding;
    targetField: FieldViewModel;
    _targetCropping: FieldViewModel;
    targetCroppingList: FieldViewModel[];

    private _newLayerInit: boolean

    private allLayers: LayerViewModel[];

    constructor(private $parse: ng.IParseService, private svc: IDatalayersService, cmd: EditLayerCmd, public readonly map: DatalayersMap) {

        this.target = cmd.layer;

        if (this.target.DataLayerId) {
            this.target = { ...cmd.layer }
        }

        this.config = cmd.config ?? {}

        this.targetField = cmd.targetField;
        this.holding = cmd.holding;

        this.targetCropping = null;
        this.targetCroppingList = (this.targetField ? this.targetField.croppings.filter(c => c.currentYear && c.visible) : []);

        if (this.isCroppingLayer) {

            this.targetCropping = cmd.targetCropping;

            if (!this.targetCropping && this.target.CroppingId) {
                this.targetCropping = this.targetField.croppings.find(c => c.croppingId == this.target.CroppingId);
            }

        }

        this.allLayers = cmd.availableLayers;

        if (this.newLayer) {
            if (this.targetField) this.target.FieldId = this.targetField.fieldId
            if (this.targetCropping) this.target.CroppingId = this.targetField.croppingId
            if (this.holding) this.target.HoldingId = this.holding.HoldingId
        }

        this.target.Crop = this.target.Crop
            || (this.targetCropping && this.targetCropping.crop)
            || svc.crops.find(c => c.CropId == this.target.CropId)

        this.outerGeometry = getTargetBoundary(this.targetCropping || this.targetField)

    }

    get isHoldingLayer(): boolean {
        return !this.targetField && !this.targetCropping && !!this.holding;
    }

    get newLayer(): boolean {
        return (!(this.target && this.target.DataLayerId));
    }

    get newLayerInit(): boolean {
        return this._newLayerInit
    }

    get targetCropping() {
        return this._targetCropping
    }

    set targetCropping(value: FieldViewModel) {
        this._targetCropping = value
        this.outerGeometry = getTargetBoundary(this.targetCropping || this.targetField)
    }

    get isCroppingLayer(): boolean {
        return (this.target && this.target.Type && this.target.Type.Scope == m.MapScope.Cropping);
    }

    hasType(): boolean {
        return !!this.target && !!this.target.Type;
    }

    get layerType(): IDataLayerType {
        return this.target.Type;
    }


    public setupNewTargetLayer() {

        if (!this.newLayer) return;

        const type = this.layerType;
        if (!type) return;

        this._newLayerInit = true;

        if (this.target.Name) return;

        if ((type.YearApplies || type.YearlyUnique) && (!this.target.FirstYearId)) {
            this.target.FirstYearId = this.svc.yearId;
        }

        if (type.LayerName && type.LayerName.DefaultNameTemplate) {

            var tmpl = this.$parse(type.LayerName.DefaultNameTemplate);

            var context = {
                type: type,
                yearId: this.svc.yearId,
            };

            this.target.Name = tmpl(context);

        }

    }

    public startEditingNewLayer(layer: Datalayer, zoneEditor: IZoneEditor) {

        let type = layer.type;
        if (!type) return;

        if (layer instanceof DrawingLayer) {

            layer.clipFeatures = !this.isHoldingLayer;

        }
        else if (!type.GenericFeatures) {

            let f = this.getContainingFeature();
            if (!f) return;

            if (layer.zones.length == 0) {

                const zone = layer.createNewZoneWithGeometry(f.getGeometry().clone())

                // TODO: généraliser via types de layers dédiés
                switch (this.layerType.TypeId) {

                    case LayerTypeId.Yield:
                        if (this.targetCropping) {
                            zone.value = this.targetCropping.cropping.TargetYield;
                        }
                        break;

                    case LayerTypeId.SoilType:
                        zone.selectDiscreteValue(this.targetField.field.SoilTypeId);
                        break;

                    case LayerTypeId.SubsoilType:
                        zone.selectDiscreteValue(this.targetField.field.SubsoilTypeId);
                        break;
                }

                if (zoneEditor) {
                    zoneEditor.setCurrentZone(zone);
                }

            }

            this.setupNewTargetLayer();


        }

        this._newLayerInit = true

    }

    public getContainingFeature(): any {

        const { map, targetCropping, targetField } = this

        let targetFeature = null;

        if (this.isCroppingLayer && targetCropping) {
            targetFeature = map.findFeatureWithData(targetCropping.croppingId)
        }

        if ((!targetFeature) && targetField) {
            targetFeature = map.fieldsLayer.findField(targetField.fieldId)
        }

        return targetFeature

    }


    public validate(): ILayerValidationResults {

        var r: ILayerValidationResults = {

            nameNotUnique: this.validateUniqueName()

        }

        for (var p in r) {
            if (r[p]) return r;
        }

    }


    private validateUniqueName(): boolean {

        if (this.layerType.LayerName && this.layerType.LayerName.Unique) {

            var name = (this.target.Name || "").toLocaleLowerCase();
            var typeId = this.layerType.TypeId;
            var layerId = this.target.DataLayerId;
            var holdingId = this.holding ? this.holding.HoldingId : 0;
            var fieldId = this.targetField ? this.targetField.fieldId : 0;
            var croppingId = this.targetCropping ? this.targetCropping.croppingId : 0;

            return this.allLayers
                .filter(l => (l.datalayerId != layerId) && l.checkTypeId(typeId) && (l.holdingId == holdingId) && (l.fieldId == fieldId) && (l.croppingId == croppingId))
                .some(l => l.datalayer.Name && (l.datalayer.Name.toLocaleLowerCase() == name));

        }

    }

}

function getTargetBoundary(target: FieldViewModel) {
    return convertGeometryToOpenLayers(target?.getBoundary())
}
